import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, ChevronRight, FileText, Info, Search, Settings } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAlertSettings } from "../../api/alertSettings";
import { getReceipt, updateCilChangeEvent } from "@hp/api-client";
import type { CilChangeEvent, CilDecision, CilDirection, CilDocumentLink, CilView, Receipt } from "@hp/types";
import { Badge, Button, Card, CardBody, Dialog } from "@hp/ui";
import { fetchCilChangeEvent, fetchCilChangeEvents, fetchCilCoverage, fetchCilOrderSummary } from "../../api/cil";
import { EmptyState, ErrorState, LoadingState } from "../../components/Feedback";
import { PageHeader } from "../../components/layout/PageHeader";
import { SourceDocumentViewer } from "../../components/SourceDocumentViewer";
import { ChangeEventJustification } from "../../components/cil/ChangeEventJustification";
import { DocumentLinkButton, DocumentLinks, LinkedText, PartyName } from "../../components/cil/DocumentLinks";
import { ConfidenceMeter, confidencePercent, confidenceTone } from "../../components/cil/ConfidenceMeter";
import { CLAIMS_CHANGED_MESSAGE } from "../../lib/demoMode";
import { HoverHint } from "../../components/HoverHint";
import { useShowConfidenceInfo } from "../../lib/useShowConfidenceInfo";
import { formatDate } from "../../lib/format";

const COPY: Record<CilDirection, { title: string; description: string; empty: string }> = {
  disallowed_to_allowed: {
    title: "Changed events from Disallowed",
    description:
      "Costs HP was refused where a ruling passed after HP's order - by a court or commission whose decisions bind HP's regulator - now allows them. Confirm to open a claim.",
    empty: "No ruling has yet allowed something HP was refused.",
  },
  allowed_to_disallowed: {
    title: "Changed events from Allowed",
    description:
      "Costs HP was granted that a ruling passed after HP's order refuses, or where the ruling HP relied on has since been stayed or overturned. Review HP's exposure.",
    empty: "Nothing HP was granted has been refused, stayed or overturned.",
  },
};

const POLARITY: Record<string, { label: string; tone: "success" | "danger" | "warning" | "neutral" | "info" }> = {
  allowed: { label: "Allowed", tone: "success" },
  disallowed: { label: "Not allowed", tone: "danger" },
  partly_allowed: { label: "Partly allowed", tone: "warning" },
  deferred: { label: "Deferred (liberty granted)", tone: "neutral" },
  set_aside: { label: "Earlier order set aside", tone: "success" },
  upheld: { label: "Earlier order upheld", tone: "neutral" },
  stayed: { label: "Stayed", tone: "warning" },
  pending: { label: "Appeal pending", tone: "info" },
};

/** What each label means, shown on hover. */
const POLARITY_HINT: Record<string, string> = {
  allowed: "The forum allowed this cost.",
  disallowed: "The forum refused this cost.",
  partly_allowed: "The forum allowed only part of the claim.",
  deferred: "The forum did not decide yet; it gave liberty to come back with more details.",
  set_aside: "An appeal court cancelled the earlier order on this point.",
  upheld: "An appeal court kept the earlier order as it was.",
  stayed: "A higher court put the ruling on hold; it cannot be given effect for now.",
  pending: "An appeal against the ruling is pending; no decision yet.",
};
const STATUS_HINT: Record<string, string> = {
  new: "Raised by the system; nobody has looked at it yet.",
  under_review: "A reviewer clicked \"Mark under review\" and is checking it.",
  confirmed: "A reviewer accepted it (for an opportunity, a claim is opened).",
  dismissed: "A reviewer rejected it as not relevant to HP, with a reason. Not in the card's number.",
  realised: "Closed automatically: HP's own later order already delivered what this was about.",
  superseded: "Closed automatically: the ruling or decision behind it no longer applies (overturned, stayed, withdrawn or re-read), or HP's position changed.",
};
const STRENGTH_HINT = {
  confirmed: "The ruling behind this change is final: no appeal is pending against it.",
  provisional: "The ruling behind this change is under appeal or on hold, so it may still change.",
};
const NOT_CLAIMED_HINT = "None of HP's orders read so far decides this cost, so there is no HP order to compare. Another generator won it in a ruling HP can use, and HP's contract gives a route to claim it: a possible new claim, if HP's plant bears this cost.";
const TERRITORY: Record<string, string> = { MP: "Madhya Pradesh", CENTRAL: "HP's CERC-regulated contracts", UP: "Uttar Pradesh", KA: "Karnataka" };
/** Which HP contract an event is about, in words: the same cost can be a
 * separate claim under each contract (different buyer and regulator). */
const CONTRACT: Record<string, string> = {
  MP: "Madhya Pradesh contract · MPERC",
  CENTRAL: "Case-1 contract · CERC",
  UP: "Uttar Pradesh contract · UPERC",
  KA: "Karnataka contract · KERC",
};

function ContractLine({ event }: { event: CilChangeEvent }) {
  const label = CONTRACT[event.jurisdiction] ?? event.jurisdiction;
  const hint = event.route
    ? `HP's contract this event is about: ${event.route}. The same cost under another HP contract is a separate claim (different buyer and regulator), so it is listed separately.`
    : "The HP contract territory this event is about. The same cost under another HP contract is a separate claim, so it is listed separately.";
  return (
    <HoverHint hint={hint}>
      <span>{label}</span>
    </HoverHint>
  );
}
const PERSUASIVE_HINT = "From another state's commission: HP's regulator may follow it but is not bound to. For reference: not in the card's number.";
/** Why an open event is not counted on the card. */
function notCountedHint(event: CilChangeEvent): string {
  const hpDate = event.baseline?.order_date;
  const otherDate = event.trigger?.order_date;
  if (event.trigger_scope === "appellate_status" && hpDate && otherDate && otherDate < hpDate) {
    return "For reference, not in the card's number: the stay or reversal came before HP's order, so nothing changed after HP was allowed. Listed because HP still cannot recover the amount while it stands.";
  }
  if (event.trigger_scope !== "self" && hpDate && otherDate && otherDate < hpDate) {
    return "For reference, not in the card's number: this ruling is older than HP's order, so nothing changed after HP's order. Listed as a ruling HP can cite (in a review, an appeal or its next true-up).";
  }
  if (event.binding !== "binding") return "For reference, not in the card's number: the ruling is from another state's commission, which HP's regulator may follow but is not bound to.";
  return "For reference, not in the card's number: an appeal that is only pending does not change HP's position yet.";
}

/** What clicking each review action does, shown on hover. */
const ACTION_HINT = {
  review: "Tells the team you are checking this one. Moves it to \"Under review\"; it stays on the card. Nothing else changes.",
  confirmClaim: "Accepts this change as relevant to HP and opens a claim from it, priced with the usual scoring. Moves it to \"Confirmed\"; it stays on the card.",
  confirmRisk: "Accepts that HP's allowance is at risk. Moves it to \"Confirmed\" for follow-up; it stays on the card. Nothing is filed or sent.",
  reject: "Opens a box for your reason. Rejecting marks it as not relevant to HP, moves it to \"Rejected\" and takes it off the card. You can reopen it later.",
  rejectSubmit: "Saves your reason, moves this to \"Rejected\" and takes it off the card. No claim or ruling is changed. You can reopen it later.",
  reopen: "Puts it back to \"Needs review\" and onto the card.",
  unconfirm: "Takes the confirmation back and returns it to \"Under review\"; it stays on the card.",
  reopenClosed: "Takes it out of \"Closed\" and puts it back \"Under review\" and onto the card. The system will not close it again by itself; you decide.",
};

// The info button next to the confidence score (and its "how this score is worked out" panel), the cap note and each
// ruling's own "Reading confidence" bar show only when Settings > Dashboard home > Show confidence score information is ticked.
/** The "For reference" badge on the event page is hidden for now; flip to show it. */
const SHOW_REFERENCE_BADGE = false;

/** "How was this produced?" (the technical receipt) is hidden for now; flip to show it again. */
const SHOW_RECEIPT = false;

type ActionKey = "review" | "confirm" | "reject" | "reopen" | "unconfirm" | "reopenClosed";
type ActionStatus = "under_review" | "confirmed" | "dismissed" | "new";

/** What each review action does, for its confirmation popup. */
function actionCopy(action: ActionKey, event: CilChangeEvent): { status: ActionStatus; title: string; body: string; confirm: string; tone: "primary" | "danger" | "amber" | "outline" } {
  const opportunity = event.direction === "disallowed_to_allowed";
  switch (action) {
    case "review":
      return { status: "under_review", title: "Mark as under review?", body: ACTION_HINT.review, confirm: "Mark under review", tone: "amber" };
    case "confirm":
      return opportunity
        ? {
            status: "confirmed",
            title: "Confirm and open a claim?",
            body: `${ACTION_HINT.confirmClaim} If a claim cannot be priced yet, the event is still confirmed and the reason is kept in its history.`,
            confirm: "Confirm & open claim",
            tone: "primary",
          }
        : { status: "confirmed", title: "Confirm this risk?", body: ACTION_HINT.confirmRisk, confirm: "Confirm risk", tone: "primary" };
    case "reject":
      return {
        status: "dismissed",
        title: "Reject this changed event?",
        body: "Rejecting marks it as not relevant to HP, moves it to \"Rejected\" and takes it off the card. No claim or ruling is changed. You can reopen it later.",
        confirm: "Reject",
        tone: "danger",
      };
    case "reopen":
      return { status: "new", title: "Reopen this changed event?", body: ACTION_HINT.reopen, confirm: "Reopen", tone: "outline" };
    case "reopenClosed":
      return {
        status: "under_review",
        title: "Move back to under review?",
        body: `${ACTION_HINT.reopenClosed} It was closed because: ${event.closed_reason ?? CLOSED_NOTE[event.status] ?? "it no longer applied."}`,
        confirm: "Move back to under review",
        tone: "amber",
      };
    case "unconfirm":
      return {
        status: "under_review",
        title: "Move back to under review?",
        body: opportunity
          ? `${ACTION_HINT.unconfirm} A claim already opened from it is kept (not deleted); confirming again will not open a second one.`
          : `${ACTION_HINT.unconfirm} Nothing else changes.`,
        confirm: "Move back to under review",
        tone: "amber",
      };
  }
}

const AMBER_BUTTON = "border border-amber-600/80 !bg-amber-500 !text-white shadow-xs hover:!bg-amber-600";

const FEEDBACK_DISCLAIMER =
  "Your reason is saved with your name in this event's history. Reviewer feedback like this is used to improve how the system reads orders, and may be used to train future versions of its models. Do not include personal or confidential details that should not be kept.";

/** Confirmation popup for a review action; Reject also asks for a reason. */
function ActionDialog({
  action,
  event,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  action: ActionKey;
  event: CilChangeEvent;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: (body: { status: ActionStatus; reason?: string }) => void;
}) {
  const copy = actionCopy(action, event);
  const [reason, setReason] = React.useState("");
  const needsReason = action === "reject";
  const ready = !pending && (!needsReason || reason.trim().length > 0);
  const submit = () => {
    if (ready) onConfirm(needsReason ? { status: copy.status, reason: reason.trim() } : { status: copy.status });
  };
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
      title={copy.title}
      description={event.event_label}
      titleId="cil-action-dialog-title"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={copy.tone === "danger" ? "danger" : copy.tone === "outline" ? "outline" : copy.tone === "amber" ? "subtle" : "primary"}
            className={copy.tone === "amber" ? AMBER_BUTTON : undefined}
            disabled={!ready}
            onClick={submit}
          >
            {pending ? "Saving..." : copy.confirm}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-zinc-700">
        <p>{copy.body}</p>
        {needsReason ? (
          <>
            <label className="flex flex-col gap-1">
              <span className="font-medium text-zinc-900">Why is this not relevant to HP? (required)</span>
              <textarea
                className="min-h-[88px] rounded-md border border-zinc-300 px-3 py-2 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="For example: the ruling is about a different plant, or the cost is not the one HP claimed."
                autoFocus
              />
            </label>
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <span className="font-semibold">Note: </span>
              {FEEDBACK_DISCLAIMER}
            </p>
          </>
        ) : null}
        {error ? <ErrorState error={error} label="Could not update" /> : null}
      </div>
    </Dialog>
  );
}

const STATUS_LABEL: Record<string, string> = {
  new: "Needs review",
  under_review: "Under review",
  confirmed: "Confirmed",
  dismissed: "Rejected",
  realised: "Achieved by HP",
  superseded: "No longer applies",
};

/** A precedent risk pairs HP's decision with a stay or reversal of the
 * ruling it relies on; any other event pairs it with a ruling that decides
 * the same cost the other way. Neither is necessarily later than HP's
 * decision (a stay can come first; a higher forum's older ruling still
 * counts), so the headings never say "earlier" or "new". */
function columnHeadings(event: CilChangeEvent): { hp: string; trigger: string } {
  if (event.trigger_scope === "appellate_status") {
    return {
      hp: "HP's allowance",
      trigger: event.trigger_kind === "reversal" ? "Reversal of the ruling HP relies on" : "Stay on the ruling HP relies on",
    };
  }
  if (event.trigger_scope === "unclaimed") return { hp: "HP's position", trigger: "Ruling that makes it claimable" };
  return { hp: "HP's decision", trigger: "Ruling that changes it" };
}

function timingNote(event: CilChangeEvent): string | null {
  const hpDate = event.baseline?.order_date;
  const otherDate = event.trigger?.order_date;
  if (!hpDate || !otherDate || otherDate > hpDate) return null;
  if (event.trigger_scope === "appellate_status") {
    const action = event.trigger_kind === "reversal" ? "reversed" : "stayed";
    return `The ${action === "stayed" ? "stay" : "reversal"} came first: the ruling HP relies on was ${action} on ${formatDate(otherDate)}, before HP's order of ${formatDate(hpDate)}. HP's allowance cannot be given effect while it stands.`;
  }
  return `This ruling (${formatDate(otherDate)}) predates HP's decision (${formatDate(hpDate)}); it counts because it comes from a higher forum.`;
}

const CLOSED_STATUSES = ["dismissed", "realised", "superseded"];
const CLOSED_NOTE: Record<string, string> = {
  dismissed: "Rejected by a reviewer.",
  realised: "Closed: HP's own later order delivered it.",
  superseded: "Closed: the ruling behind it was overturned or stayed, the decision it rested on was withdrawn or re-read, or HP's position has since changed.",
};

function periodLabel(key: string): string {
  if (key.startsWith("from:")) return `from ${formatDate(key.slice(5))}`;
  return key.replace("..", " to ");
}

function DecisionColumn({
  heading,
  decision,
  onViewSource,
}: {
  heading: string;
  decision: CilDecision;
  onViewSource: (decision: CilDecision) => void;
}) {
  const showConfidenceInfo = useShowConfidenceInfo();
  const polarity = POLARITY[decision.effective_polarity ?? decision.polarity] ?? POLARITY[decision.polarity] ?? { label: decision.polarity, tone: "neutral" as const };
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">{heading}</span>
        <HoverHint hint={POLARITY_HINT[decision.effective_polarity ?? decision.polarity] ?? POLARITY_HINT[decision.polarity] ?? polarity.label}>
          <Badge tone={polarity.tone}>{polarity.label}</Badge>
        </HoverHint>
      </div>
      <div className="text-sm text-zinc-800">
        <span className="font-medium">{decision.forum_name ?? "Unknown forum"}</span>
        {decision.case_ref ? <span className="text-zinc-600"> · {decision.case_ref}</span> : null}
        {decision.order_date ? <span className="text-zinc-600"> · {formatDate(decision.order_date)}</span> : null}
      </div>
      {decision.party_name ? (
        <div className="text-xs text-zinc-600">
          For{" "}
          <PartyName>
            {decision.party_name}
            {decision.is_hp ? " (HP)" : ""}
          </PartyName>
        </div>
      ) : null}
      <blockquote className="border-l-2 border-zinc-300 pl-3 text-sm italic text-zinc-700">
        “{decision.quote}” <span className="not-italic text-zinc-500">p{decision.page}</span>
      </blockquote>
      {decision.authority_status && decision.authority_status !== "final" ? (
        <div className="text-xs text-amber-700">Standing: {decision.authority_status.replace(/_/g, " ")}</div>
      ) : null}
      {showConfidenceInfo ? <ConfidenceMeter label="Reading confidence" value={decision.confidence} basis={decision.confidence_basis ?? []} /> : null}
      <div>
        <Button size="sm" variant="ghost" onClick={() => onViewSource(decision)} disabled={!decision.has_source_document}>
          <FileText className="h-4 w-4" aria-hidden /> {decision.has_source_document ? "View source" : "Source not stored"}
        </Button>
      </div>
    </div>
  );
}

/** Left column of an "unclaimed" event: HP has no order on the cost. */
function NotClaimedColumn({ heading, jurisdiction }: { heading: string; jurisdiction: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">{heading}</span>
        <HoverHint hint={NOT_CLAIMED_HINT}>
          <Badge tone="neutral">
            <span className="flex flex-col items-start leading-snug">
              <span>No HP order on this cost</span>
              <span>Possible new claim</span>
            </span>
          </Badge>
        </HoverHint>
      </div>
      <p className="text-sm text-zinc-700">
        None of HP's orders read so far in {TERRITORY[jurisdiction] ?? jurisdiction} decides this cost, so this is a possible new claim. HP's contract there gives a route to claim it.
      </p>
      <p className="text-xs text-amber-800">Check whether HP's plant actually bears this cost before confirming.</p>
    </div>
  );
}

function ReceiptView({ receipt, depth = 0 }: { receipt: Receipt; depth?: number }) {
  return (
    <div className={depth ? "mt-2 border-l border-zinc-200 pl-3" : ""}>
      <div className="font-mono text-[0.6875rem] text-zinc-500">{receipt.object}</div>
      {receipt.model_route?.length ? <div className="text-xs text-zinc-700">Route: {receipt.model_route.join(" → ")}</div> : null}
      {receipt.context_sources?.length ? <div className="text-xs text-zinc-700">Read: {receipt.context_sources.join(", ")}</div> : null}
      {receipt.tools_used?.length ? <div className="text-xs text-zinc-700">Tools: {receipt.tools_used.join("; ")}</div> : null}
      {receipt.verifiers && Object.keys(receipt.verifiers).length ? (
        <div className="text-xs text-zinc-700">
          Checks: {Object.entries(receipt.verifiers).map(([k, v]) => `${k} ${v}`).join(" · ")}
        </div>
      ) : null}
      {receipt.human_corrections?.length ? <div className="text-xs text-zinc-700">Reviewed: {receipt.human_corrections.length} item(s)</div> : null}
      {receipt.rule_trace ? (
        <div className="text-xs text-zinc-700">
          Rules: {Object.entries(receipt.rule_trace).filter(([k]) => ["flip", "binding", "binding_reason", "route", "authority", "event_match"].includes(k)).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
        </div>
      ) : null}
      {receipt.baseline ? <ReceiptView receipt={receipt.baseline} depth={depth + 1} /> : null}
      {receipt.trigger ? <ReceiptView receipt={receipt.trigger} depth={depth + 1} /> : null}
    </div>
  );
}

function ReceiptPanel({ eventId }: { eventId: string }) {
  const query = useQuery({ queryKey: ["cil", "receipt", eventId], queryFn: ({ signal }) => getReceipt("change_event", eventId, signal) });
  if (query.isPending) return <LoadingState label="Loading how this was produced..." />;
  if (query.isError) return <ErrorState error={query.error} label="Could not load the receipt" />;
  return <ReceiptView receipt={query.data} />;
}

function eventParties(event: CilChangeEvent): string[] {
  return [event.baseline?.party_name, event.trigger?.party_name].filter((p): p is string => Boolean(p));
}

/** A reading a person approved counts as 100% when the backend says so
 * (`checked_by` is only sent with CIL_PERSON_CHECK_IS_100 on). */
function readingScore(decision: CilDecision): number {
  return decision.checked_by ? 1 : decision.confidence;
}

/** With no HP order on the cost, HP's position (and whether HP bears the
 * cost at all) is unknown, so the event is capped until a person confirms
 * it: 70% when HP's own documents mention the cost (a sign HP bears it),
 * 50% when none does. The ruling's own reading score is shown in the info
 * panel. */
const NO_HP_ORDER_CAP = 0.5;
const HP_MENTIONS_CAP = 0.7;

function hpPositionUnknown(event: CilChangeEvent): boolean {
  return !event.baseline && event.status !== "confirmed";
}

function positionCap(event: CilChangeEvent): number {
  return (event.hp_mentions ?? []).length > 0 ? HP_MENTIONS_CAP : NO_HP_ORDER_CAP;
}

/** The ruling's reading, before any cap. */
function rulingScore(event: CilChangeEvent): number {
  return event.trigger ? readingScore(event.trigger) : 1;
}

/** An event is only as sure as the less certain of its two readings. */
function eventConfidence(event: CilChangeEvent): number {
  const score = Math.min(event.baseline ? readingScore(event.baseline) : 1, rulingScore(event));
  return hpPositionUnknown(event) ? Math.min(score, positionCap(event)) : score;
}

/** Why the score is capped, in a few words, for beside the score. */
function capNote(event: CilChangeEvent): string | undefined {
  if (!hpPositionUnknown(event)) return undefined;
  return (event.hp_mentions ?? []).length
    ? `capped at ${confidencePercent(HP_MENTIONS_CAP)}: no HP order on this cost, though HP's documents mention it`
    : `capped at ${confidencePercent(NO_HP_ORDER_CAP)}: no HP order on this cost, and HP's documents do not mention it`;
}

function eventConfidenceBasis(event: CilChangeEvent, headings: { hp: string; trigger: string }): string[] {
  if (!event.baseline) {
    const ruling = confidencePercent(rulingScore(event));
    return [
      `Ruling read correctly: ${ruling}.`,
      !hpPositionUnknown(event)
        ? "HP's position: a person confirmed HP bears this cost, so the score is the ruling's reading."
        : (event.hp_mentions ?? []).length
          ? `HP's position: no HP order on this cost, but HP's own documents mention it (${event.hp_mentions!.slice(0, 3).join("; ")}), which suggests HP bears it. Capped at ${confidencePercent(HP_MENTIONS_CAP)} until a person confirms.`
          : `HP's position: unknown. No HP order on this cost and none of HP's documents mention it, so the score is capped at ${confidencePercent(NO_HP_ORDER_CAP)} until a person confirms HP bears it.`,
      ...(event.trigger?.confidence_basis ?? []).map((l) => `${headings.trigger}: ${l}`),
      "The score is about how reliably the orders were read, not about how likely a claim is to succeed.",
    ];
  }
  const lines = [
    "A changed event rests on two readings: HP's decision and the ruling that changes it. It is only as sure as the less certain of the two, so the overall score is the lower one.",
    `${headings.hp}: ${confidencePercent(readingScore(event.baseline))}${event.baseline.checked_by ? " (checked by a person)" : ""}.`,
  ];
  lines.push(...(event.baseline.confidence_basis ?? []).map((l) => `${headings.hp}: ${l}`));
  if (event.trigger) {
    lines.push(`${headings.trigger}: ${confidencePercent(readingScore(event.trigger))}${event.trigger.checked_by ? " (checked by a person)" : ""}.`);
    lines.push(...(event.trigger.confidence_basis ?? []).map((l) => `${headings.trigger}: ${l}`));
  }
  lines.push("The score is about how reliably the orders were read, not about how likely a claim is to succeed.");
  return lines;
}

function ChangeEventCard({ event, onViewSource }: { event: CilChangeEvent; onViewSource: (d: CilDecision) => void }) {
  const queryClient = useQueryClient();
  const showConfidenceInfo = useShowConfidenceInfo();
  const [showReceipt, setShowReceipt] = React.useState(false);
  const [showJustification, setShowJustification] = React.useState(true);
  const [action, setAction] = React.useState<ActionKey | null>(null);
  const mutation = useMutation({
    mutationFn: (body: { status: "under_review" | "confirmed" | "dismissed" | "new"; reason?: string }) => updateCilChangeEvent(event.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cil"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (window.parent !== window) window.parent.postMessage(CLAIMS_CHANGED_MESSAGE, window.location.origin);
      setAction(null);
    },
  });
  const open = ["new", "under_review", "confirmed"].includes(event.status);
  const headings = columnHeadings(event);
  const timing = timingNote(event);
  // The longest wording of the supporting ruling ("APTEL Appeal 113/2022 of 28 Jul 2025").
  const supportingLink = (event.documents ?? [])
    .filter((l) => l.ruling_id === event.supporting_ruling?.id)
    .sort((a, b) => b.text.length - a.text.length)[0];
  return (
    <Card>
      <CardBody>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-zinc-900">{event.event_label}</h3>
              <span className="text-sm text-zinc-500">
                <ContractLine event={event} /> · {periodLabel(event.period_key)}
              </span>
              <HoverHint hint={event.strength === "confirmed" ? STRENGTH_HINT.confirmed : STRENGTH_HINT.provisional}>
                <Badge tone={event.strength === "confirmed" ? "success" : "warning"}>
                  {event.strength === "confirmed" ? "Final ruling" : "Still under appeal / stayed"}
                </Badge>
              </HoverHint>
              <HoverHint hint={STATUS_HINT[event.status] ?? event.status}>
                <Badge tone="neutral">{STATUS_LABEL[event.status] ?? event.status}</Badge>
              </HoverHint>
              {event.binding !== "binding" ? (
                <HoverHint hint={PERSUASIVE_HINT}>
                  <Badge tone="info">Persuasive only</Badge>
                </HoverHint>
              ) : null}
              {SHOW_REFERENCE_BADGE && open && !event.counts_on_card ? (
                <HoverHint hint={notCountedHint(event)}>
                  <Badge tone="neutral">For reference</Badge>
                </HoverHint>
              ) : null}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {event.status === "new" ? (
                <HoverHint align="right" hint={ACTION_HINT.review}>
                  <Button size="sm" variant="subtle" className={AMBER_BUTTON} disabled={mutation.isPending} onClick={() => setAction("review")}>
                    Mark under review
                  </Button>
                </HoverHint>
              ) : null}
              {event.status === "new" || event.status === "under_review" ? (
                <>
                  <HoverHint align="right" hint={event.direction === "disallowed_to_allowed" ? ACTION_HINT.confirmClaim : ACTION_HINT.confirmRisk}>
                    <Button size="sm" disabled={mutation.isPending} onClick={() => setAction("confirm")}>
                      {event.direction === "disallowed_to_allowed" ? "Confirm & open claim" : "Confirm risk"}
                    </Button>
                  </HoverHint>
                  <HoverHint align="right" hint={ACTION_HINT.reject}>
                    <Button size="sm" variant="danger" disabled={mutation.isPending} onClick={() => setAction("reject")}>
                      Reject
                    </Button>
                  </HoverHint>
                </>
              ) : null}
              {event.status === "confirmed" ? (
                <HoverHint align="right" hint={ACTION_HINT.unconfirm}>
                  <Button size="sm" variant="subtle" className={AMBER_BUTTON} disabled={mutation.isPending} onClick={() => setAction("unconfirm")}>
                    Move back to under review
                  </Button>
                </HoverHint>
              ) : null}
              {event.status === "dismissed" ? (
                <HoverHint align="right" hint={ACTION_HINT.reopen}>
                  <Button size="sm" variant="outline" disabled={mutation.isPending} onClick={() => setAction("reopen")}>
                    Reopen
                  </Button>
                </HoverHint>
              ) : null}
              {event.status === "realised" || event.status === "superseded" ? (
                <HoverHint align="right" hint={ACTION_HINT.reopenClosed}>
                  <Button size="sm" variant="subtle" className={AMBER_BUTTON} disabled={mutation.isPending} onClick={() => setAction("reopenClosed")}>
                    Move back to under review
                  </Button>
                </HoverHint>
              ) : null}
            </div>
          </div>

          {action ? (
            <ActionDialog
              key={action}
              action={action}
              event={event}
              pending={mutation.isPending}
              error={mutation.isError ? mutation.error : null}
              onCancel={() => {
                setAction(null);
                mutation.reset();
              }}
              onConfirm={(body) => mutation.mutate(body)}
            />
          ) : null}

          <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
            {event.baseline ? (
              <DecisionColumn heading={headings.hp} decision={event.baseline} onViewSource={onViewSource} />
            ) : (
              <NotClaimedColumn heading={headings.hp} jurisdiction={event.jurisdiction} />
            )}
            <div className="flex items-center justify-center text-zinc-400">
              <ArrowRight className="h-5 w-5" aria-hidden />
            </div>
            {event.trigger ? (
              <DecisionColumn heading={headings.trigger} decision={event.trigger} onViewSource={onViewSource} />
            ) : null}
          </div>
          <ConfidenceMeter
            label={hpPositionUnknown(event) ? "Confidence (ruling read only)" : "Overall confidence"}
            value={eventConfidence(event)}
            basis={showConfidenceInfo ? eventConfidenceBasis(event, headings) : []}
            note={showConfidenceInfo ? capNote(event) : undefined}
          />
          {timing && !showJustification ? <p className="text-sm text-zinc-700">{timing}</p> : null}

          {event.supporting_ruling ? (
            <p className="text-sm text-zinc-700">
              HP's position relies on{" "}
              {supportingLink ? (
                <DocumentLinkButton link={supportingLink} />
              ) : (
                `${event.supporting_ruling.forum_name} ${event.supporting_ruling.case_ref ?? ""}`.trim()
              )}
              {event.supporting_ruling.is_placeholder ? " (not uploaded yet)" : ""}.
            </p>
          ) : null}

          {showJustification ? (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-3">
              <ChangeEventJustification eventId={event.id} onViewSource={onViewSource} />
            </div>
          ) : (
            <p className="text-sm text-zinc-700">
              <span className="font-medium">Why this matters: </span>
              <LinkedText text={event.relevance_note} />
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowJustification((v) => !v)} aria-expanded={showJustification}>
              {showJustification ? "Hide full justification" : "Show full justification"}
            </Button>
            {SHOW_RECEIPT ? (
              <Button size="sm" variant="ghost" onClick={() => setShowReceipt((v) => !v)}>
                <Info className="h-4 w-4" aria-hidden /> How was this produced?
              </Button>
            ) : null}
            {event.claim_candidate_id ? (
              <HoverHint hint="A claim was opened from this event when it was confirmed.">
                <Badge tone="success">Claim opened</Badge>
              </HoverHint>
            ) : null}
          </div>

          {SHOW_RECEIPT && showReceipt ? (
            <div className="rounded-md border border-zinc-200 bg-white p-3">
              <ReceiptPanel eventId={event.id} />
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

/** Whether the other ruling came before or after HP's order: the table
 * compares the two, it is not a before → after sequence. */
function triggerTiming(event: CilChangeEvent): { label: string; hint: string } | null {
  const hpDate = event.baseline?.order_date;
  const otherDate = event.trigger?.order_date;
  if (!hpDate || !otherDate) return null;
  if (otherDate < hpDate) {
    return {
      label: "Came before HP's order",
      hint:
        event.trigger_scope === "appellate_status"
          ? "This stay or reversal was passed before HP's order: HP was allowed while it already stood, so nothing changed after HP's order. Listed under For reference; HP still cannot recover the amount while it stands."
          : "This ruling is older than HP's order but comes from a forum whose rulings bind (or guide) HP's regulator, so it still counts. HP's order did not give the same result.",
    };
  }
  if (otherDate > hpDate) return { label: "Came after HP's order", hint: "This ruling was passed after HP's order." };
  return { label: "Same day as HP's order", hint: "This ruling was passed on the same day as HP's order." };
}

function RulingCell({ decision, timing }: { decision: CilDecision | null; timing?: { label: string; hint: string } | null }) {
  if (!decision) {
    return (
      <HoverHint hint={NOT_CLAIMED_HINT}>
        <Badge tone="neutral">
            <span className="flex flex-col items-start leading-snug">
              <span>No HP order on this cost</span>
              <span>Possible new claim</span>
            </span>
          </Badge>
      </HoverHint>
    );
  }
  const polarity = POLARITY[decision.effective_polarity ?? decision.polarity] ?? POLARITY[decision.polarity] ?? { label: decision.polarity, tone: "neutral" as const };
  return (
    <div className="flex flex-col gap-1">
      <HoverHint hint={POLARITY_HINT[decision.effective_polarity ?? decision.polarity] ?? POLARITY_HINT[decision.polarity] ?? polarity.label}>
          <Badge tone={polarity.tone}>{polarity.label}</Badge>
        </HoverHint>
      <span className="text-xs text-zinc-700">
        {decision.forum_name === "SC" ? "Supreme Court" : decision.forum_name ?? "Unknown forum"}
        {decision.order_date ? ` · ${formatDate(decision.order_date)}` : ""}
      </span>
      {decision.case_ref ? <span className="text-xs text-zinc-500">{decision.case_ref}</span> : null}
      {timing ? (
        <HoverHint hint={timing.hint}>
          <span className="text-xs font-medium text-sky-800">{timing.label}</span>
        </HoverHint>
      ) : null}
      {decision.party_name && !decision.is_hp ? (
        <span className="text-xs text-zinc-500">
          For <PartyName>{decision.party_name}</PartyName>
        </span>
      ) : null}
    </div>
  );
}

/** One row per changed event: what HP had, what changed it, and what that
 * means for HP. Clicking a row opens the event's own page (both rulings,
 * the full justification, actions) in the same window, with Back. */
/** The columns of the events table, in order. `onByDefault` false: shown only when ticked in the gear menu. `locked`: always shown, cannot be unticked. */
const TABLE_COLUMNS = [
  { key: "event", label: "Events", onByDefault: true, locked: true },
  { key: "baseline", label: "HP's decision", onByDefault: true, locked: true },
  { key: "trigger", label: "Compared with", onByDefault: true, locked: true },
  { key: "change", label: "Change", onByDefault: true, locked: true },
  { key: "outcome", label: "Outcome from change", onByDefault: false, locked: false },
  { key: "confidence", label: "Confidence", onByDefault: true, locked: true },
  { key: "effect", label: "Effect for HP", onByDefault: false, locked: false },
] as const;
type ColumnKey = (typeof TABLE_COLUMNS)[number]["key"];
const DEFAULT_COLUMNS: ColumnKey[] = TABLE_COLUMNS.filter((c) => c.onByDefault).map((c) => c.key);
const COLUMNS_STORAGE_KEY = "hp.overview.columns";

/** Which columns the Overview table shows: the five locked ones always, plus the optional ones the person ticked; remembered in this browser. */
function useColumnChoice(): { columns: ReadonlySet<ColumnKey>; toggle: (key: ColumnKey) => void; reset: () => void } {
  const [chosen, setChosen] = React.useState<ColumnKey[]>(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(COLUMNS_STORAGE_KEY) ?? "null");
      const valid = Array.isArray(saved) ? saved.filter((k): k is ColumnKey => TABLE_COLUMNS.some((c) => c.key === k)) : [];
      return withLocked(valid);
    } catch {
      return DEFAULT_COLUMNS;
    }
  });
  const save = (next: ColumnKey[]) => {
    next = withLocked(next);
    setChosen(next);
    try {
      window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* the choice then lasts until the page is left */
    }
  };
  return {
    columns: React.useMemo(() => new Set(chosen), [chosen]),
    toggle: (key) => {
      if (TABLE_COLUMNS.find((c) => c.key === key)?.locked) return; // Events, HP's decision, Compared with, Change and Confidence always show
      save(chosen.includes(key) ? chosen.filter((k) => k !== key) : [...chosen, key]);
    },
    reset: () => save(DEFAULT_COLUMNS),
  };
}

/** The columns in table order, with the locked ones always included. */
function withLocked(keys: ColumnKey[]): ColumnKey[] {
  return TABLE_COLUMNS.filter((c) => c.locked || keys.includes(c.key)).map((c) => c.key);
}

function ImpactTable({ events, detailHref, heading = true, columns }: { events: CilChangeEvent[]; detailHref: (event: CilChangeEvent) => string; heading?: boolean; columns?: ReadonlySet<ColumnKey> }) {
  const showConfidenceInfo = useShowConfidenceInfo();
  const navigate = useNavigate();
  const show = (key: ColumnKey) => !columns || columns.has(key);
  return (
    <Card>
      <CardBody>
        {heading ? (
          <>
            <h3 className="mb-1 text-sm font-semibold text-zinc-900">How these changes affect HP</h3>
            <p className="mb-2 text-xs text-zinc-500">Click a row to open its rulings, full justification and actions.</p>
          </>
        ) : null}
        <div className="overflow-x-auto">
          <table className={["w-full text-left text-sm", columns ? "min-w-[640px]" : "min-w-[980px]"].join(" ")}>
            <thead>
              <tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
                {TABLE_COLUMNS.filter((c) => show(c.key)).map((c) => (
                  <th key={c.key} className="px-2 py-2 font-medium">{c.label}</th>
                ))}
                <th className="w-8 px-1 py-2" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr
                  key={event.id}
                  className="cursor-pointer border-b border-zinc-100 align-top last:border-0 hover:bg-zinc-50"
                  onClick={() => navigate(detailHref(event))}
                >
                  {show("event") ? (
                  <td className="px-2 py-2">
                    <div className="font-medium text-zinc-900">{event.event_label}</div>
                    {event.source_description || event.case_name ? (
                      <div className="mt-0.5 max-w-[34rem] text-xs font-medium leading-snug text-sky-800">{event.source_description || event.case_name}</div>
                    ) : null}
                    {event.impact.outcome ? <p className="mt-0.5 max-w-[34rem] text-xs leading-snug text-zinc-600">{event.impact.outcome}</p> : null}
                    <div className="mt-0.5 text-xs text-zinc-500">
                      <ContractLine event={event} /> · {periodLabel(event.period_key)}
                    </div>
                  </td>
                  ) : null}
                  {show("baseline") ? (
                  <td className="px-2 py-2">
                    <RulingCell decision={event.baseline} />
                  </td>
                  ) : null}
                  {show("trigger") ? (
                  <td className="px-2 py-2">
                    <RulingCell decision={event.trigger} timing={triggerTiming(event)} />
                  </td>
                  ) : null}
                  {show("change") ? (
                  <td className="px-2 py-2 font-medium whitespace-nowrap text-zinc-900">
                    {/* A note in brackets ("(HP's own later order)") goes on its own line. */}
                    {event.impact.change.split(/ (?=\()/).map((part, i) => (
                      <div key={i}>{part}</div>
                    ))}
                  </td>
                  ) : null}
                  {show("outcome") ? (
                  <td className="px-2 py-2 font-medium text-zinc-900">
                    {CLOSED_STATUSES.includes(event.status) ? (
                      <span className="font-normal text-zinc-600">{event.closed_reason ?? CLOSED_NOTE[event.status] ?? "Closed."}</span>
                    ) : (
                      <>
                        {event.impact.outcome ?? ""}
                        {event.closing_suggestion ? (
                          <span className="mt-1 block text-xs font-normal text-amber-700">Suggested to close: {event.closing_suggestion}. Reject it if you agree.</span>
                        ) : null}
                      </>
                    )}
                  </td>
                  ) : null}
                  {show("confidence") ? (
                  <td className="px-2 py-2 whitespace-nowrap">
                    <HoverHint hint={event.baseline ? `How reliably the two orders were read: the lower of the two readings' scores.${showConfidenceInfo ? " Open the row for how it is worked out." : ""}` : `How reliably the ruling was read (HP has no order on this cost)${showConfidenceInfo && capNote(event) ? `; ${capNote(event)}` : ""}.${showConfidenceInfo ? " Open the row for how it is worked out." : ""}`}>
                      <span className={`font-semibold tabular-nums ${confidenceTone(eventConfidence(event)).text}`}>
                        {confidencePercent(eventConfidence(event))}
                        <span className="ml-1 text-xs font-normal">({confidenceTone(eventConfidence(event)).word})</span>
                      </span>
                    </HoverHint>
                  </td>
                  ) : null}
                  {show("effect") ? (
                  <td className="px-2 py-2 text-zinc-700">
                    <DocumentLinks links={event.documents ?? []} parties={eventParties(event)}>
                      <LinkedText text={event.impact.effect} />
                    </DocumentLinks>
                  </td>
                  ) : null}
                  <td className="px-1 py-2 text-right">
                    <Link
                      to={detailHref(event)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Open ${event.event_label}`}
                      className="inline-flex rounded p-0.5 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

type ListTab = "needs_review" | "under_review" | "confirmed" | "rejected" | "closed";

const OPEN = ["new", "under_review", "confirmed"];

const TABS: Array<{ view: ListTab; label: string; hint: string; empty: string; keep: (e: CilChangeEvent) => boolean; hideWhenEmpty?: boolean }> = [
  { view: "needs_review", label: "Needs review", hint: "Raised by the system; nobody has looked at these yet. Includes items kept for reference (not in the card's number): the badge on each says why.", empty: "Nothing waiting for review.", keep: (e) => e.status === "new" },
  { view: "under_review", label: "Under review", hint: "A reviewer clicked \"Mark under review\" and is checking these.", empty: "Nothing under review.", keep: (e) => e.status === "under_review" },
  { view: "confirmed", label: "Confirmed", hint: "Accepted by a reviewer; for an opportunity a claim is opened.", empty: "Nothing confirmed yet.", keep: (e) => e.status === "confirmed" },
  { view: "rejected", label: "Rejected", hint: "Rejected by a reviewer as not relevant to HP, with a reason. Not in the card's number.", empty: "Nothing rejected.", keep: (e) => e.status === "dismissed" },
  {
    view: "closed", label: "Closed", hint: "Events the system closed because they are not valid changes: the rules no longer raise them, an HP order already delivered them, or a review found them misread or duplicated. Each says why. Only events a person rejected are under Rejected.",
    empty: "Nothing closed.", keep: (e) => e.status === "realised" || e.status === "superseded",
  },
];

const SLUG: Record<CilDirection, string> = {
  disallowed_to_allowed: "cil-disallowed-to-allowed",
  allowed_to_disallowed: "cil-allowed-to-disallowed",
};
const LEGACY_TABS: Record<string, ListTab> = { counted: "needs_review", on_card: "needs_review", watching: "needs_review", not_counted: "needs_review" };

/** The tab named in the URL, or null when none is (the page then opens
 * the first tab that has anything in it). */
function viewFrom(params: URLSearchParams): ListTab | null {
  const value = params.get("view") ?? "";
  const tab = LEGACY_TABS[value] ?? value;
  return TABS.some((t) => t.view === tab) ? (tab as ListTab) : null;
}

/** The list's tab, search and sort as a query string, so opening an event and coming Back restores them. */
function listQuery(view: ListTab | null, params?: URLSearchParams): string {
  const out = new URLSearchParams();
  if (view) out.set("view", view);
  const q = params?.get("q");
  if (q) out.set("q", q);
  const sort = params?.get("sort");
  if (sort) out.set("sort", sort);
  const dir = params?.get("dir");
  if (dir) out.set("dir", dir);
  const text = out.toString();
  return text ? `?${text}` : "";
}

/** How the popup's list can be ordered (in the browser, over the events already loaded). */
const SORT_FIELDS = [
  { key: "date", label: "Ruling date", defaultDir: "desc" },
  { key: "confidence", label: "Confidence", defaultDir: "desc" },
  { key: "cost", label: "Cost", defaultDir: "asc" },
  { key: "where", label: "Contract / state", defaultDir: "asc" },
] as const;
type SortKey = (typeof SORT_FIELDS)[number]["key"];
type SortDir = "asc" | "desc";

function sortFrom(params: URLSearchParams, fallback: SortKey = "date"): SortKey {
  const value = params.get("sort") ?? "";
  return (SORT_FIELDS.find((s) => s.key === value)?.key ?? fallback) as SortKey;
}

function defaultDir(field: SortKey): SortDir {
  return SORT_FIELDS.find((s) => s.key === field)?.defaultDir ?? "desc";
}

/** ↑ ascending (oldest / lowest / A to Z first), ↓ descending (newest / highest first). */
function dirFrom(params: URLSearchParams, field: SortKey): SortDir {
  const value = params.get("dir");
  return value === "asc" || value === "desc" ? value : defaultDir(field);
}

function rulingTime(event: CilChangeEvent): number {
  const date = event.trigger?.order_date;
  return date ? new Date(date).getTime() : Number.NEGATIVE_INFINITY;
}

function sortEvents(events: CilChangeEvent[], field: SortKey, dir: SortDir): CilChangeEvent[] {
  const byNewest = (a: CilChangeEvent, b: CilChangeEvent) => rulingTime(b) - rulingTime(a) || Date.parse(b.first_detected_at) - Date.parse(a.first_detected_at);
  // Ascending order of each field; the chosen direction flips it, ties fall back to newest ruling first.
  const ascending: Record<SortKey, (a: CilChangeEvent, b: CilChangeEvent) => number> = {
    date: (a, b) => rulingTime(a) - rulingTime(b),
    confidence: (a, b) => eventConfidence(a) - eventConfidence(b),
    cost: (a, b) => a.event_label.localeCompare(b.event_label),
    where: (a, b) => a.jurisdiction.localeCompare(b.jurisdiction) || a.event_label.localeCompare(b.event_label),
  };
  const sign = dir === "asc" ? 1 : -1;
  return [...events].sort((a, b) => sign * ascending[field](a, b) || byNewest(a, b));
}

/** Everything a person might type to find an event: cost, place, period, parties, forums, case numbers, the ruling's own words, the names of the files it was read from. */
function searchText(event: CilChangeEvent): string {
  const decision = (d: CilDecision | null | undefined) => (d ? [d.party_name, d.forum_name, d.case_ref, d.event_phrase, d.quote, d.period_key, d.file_name].join(" ") : "");
  return [
    event.event_label, event.case_name ?? "", event.source_description ?? "", event.event_key, event.jurisdiction, event.period_key, event.relevance_note, event.impact.change, event.impact.effect,
    event.route ?? "", decision(event.baseline), decision(event.trigger),
  ]
    .join(" ")
    .toLowerCase();
}

function matchesSearch(event: CilChangeEvent, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = searchText(event);
  return words.every((w) => text.includes(w));
}

/** Search box and "Sort by" menu, on the right of the tabs. */
function ListToolbar({
  query,
  onQuery,
  sort,
  onSort,
  dir,
  shown,
  total,
  columnChoice,
}: {
  query: string;
  onQuery: (value: string) => void;
  sort: SortKey;
  onSort: (field: SortKey, direction: SortDir) => void;
  dir: SortDir;
  shown: number;
  total: number;
  /** When given, a gear on the right of "Sort by" chooses which columns the table shows. */
  columnChoice?: { columns: ReadonlySet<ColumnKey>; toggle: (key: ColumnKey) => void; reset: () => void };
}) {
  return (
    // Fixed-size parts on one line, flush with the top right corner beside the tabs: nothing here changes width while typing.
    <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:flex-nowrap lg:justify-end">
      <div className="relative min-w-[8rem] flex-1 basis-full sm:basis-0 lg:flex-none lg:basis-auto">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search events or source file..."
          aria-label="Search the changed events"
          className="h-9 w-full rounded-md border border-zinc-300 bg-white pl-8 pr-14 lg:w-56 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none"
        />
        {/* The match count sits inside the box, so nothing is reserved to the right of the menu. */}
        {query.trim() ? (
          <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 whitespace-nowrap text-xs tabular-nums text-zinc-500" aria-live="polite">
            {shown}/{total}
          </span>
        ) : null}
      </div>
      <label className="flex items-center gap-1.5 text-sm text-zinc-600">
        <span className="whitespace-nowrap">Sort by</span>
        {/* The browser's own arrow is hidden and drawn here, so it can sit a comfortable distance from the right edge. */}
        <span className="relative inline-block">
          <select
            value={`${sort}:${dir}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split(":") as [SortKey, SortDir];
              onSort(field, direction);
            }}
            aria-label="Sort the changed events by"
            title="↑ asc: oldest, lowest or A to Z first. ↓ desc: newest, highest or Z to A first."
            className="h-9 appearance-none rounded-md border border-zinc-300 bg-white pl-2.5 pr-9 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
          >
            {SORT_FIELDS.flatMap((field) =>
              (field.defaultDir === "desc" ? (["desc", "asc"] as const) : (["asc", "desc"] as const)).map((d) => (
                <option key={`${field.key}:${d}`} value={`${field.key}:${d}`}>
                  {field.label} {d === "asc" ? "↑ asc" : "↓ desc"}
                </option>
              )),
            )}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden />
        </span>
      </label>
      {columnChoice ? <ColumnsMenu choice={columnChoice} /> : null}
    </div>
  );
}

/** A gear that opens a small menu of the table's columns, each with a tick; closes on a click outside or Escape. */
function ColumnsMenu({ choice }: { choice: { columns: ReadonlySet<ColumnKey>; toggle: (key: ColumnKey) => void; reset: () => void } }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const isDefault = TABLE_COLUMNS.every((c) => choice.columns.has(c.key) === c.onByDefault);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Choose the columns to show"
        aria-expanded={open}
        title="Choose the columns to show"
        className={["inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50 focus:border-zinc-500 focus:outline-none", open ? "bg-zinc-100" : ""].join(" ")}
      >
        <Settings className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-zinc-200 bg-white p-2 shadow-lg">
          <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">Columns</p>
          {TABLE_COLUMNS.map((c) => (
            <label
              key={c.key}
              title={c.locked ? "Always shown" : undefined}
              className={["flex items-center gap-2 rounded px-1 py-1 text-sm", c.locked ? "cursor-not-allowed text-zinc-500" : "cursor-pointer text-zinc-800 hover:bg-zinc-50"].join(" ")}
            >
              <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={choice.columns.has(c.key)} disabled={c.locked} onChange={() => choice.toggle(c.key)} />
              {c.label}
            </label>
          ))}
          <button type="button" disabled={isDefault} onClick={choice.reset} className="mt-1 w-full rounded px-1 py-1 text-left text-xs font-medium text-[var(--color-accent-content)] hover:underline disabled:cursor-default disabled:text-zinc-400 disabled:no-underline">
            Show the default columns
          </button>
        </div>
      ) : null}
    </div>
  );
}

const FILE_COPY: Record<CilDirection, { title: string; description: string; empty: string }> = {
  disallowed_to_allowed: {
    title: "Changed events from Disallowed: this file",
    description: "Only the possible claims raised by the file you opened this from: costs HP was refused (or has no order on) that its rulings now allow.",
    empty: "This file raised no changed event from Disallowed.",
  },
  allowed_to_disallowed: {
    title: "Changed events from Allowed: this file",
    description: "Only the risks of reversal raised by the file you opened this from: costs HP was granted that its rulings put at risk.",
    empty: "This file raised no changed event from Allowed.",
  },
};

const FILE_SLUG: Record<string, CilDirection> = { "from-disallowed": "disallowed_to_allowed", "from-allowed": "allowed_to_disallowed" };

/** The changed events raised by one uploaded file, in one direction (the file's two links). */
export function CilFileEventsPage() {
  const { jobId = "", which = "" } = useParams();
  return <CilChangeEventsReportPage direction={FILE_SLUG[which] ?? "disallowed_to_allowed"} jobId={jobId} />;
}

export function CilChangeEventsReportPage({ direction, jobId }: { direction: CilDirection; jobId?: string }) {
  // The tab lives in the URL so Back from an event's page returns to it.
  const [params, setParams] = useSearchParams();
  const asked = viewFrom(params);
  // Every event for this direction, once; tabs filter in the browser so
  // they switch instantly and can show their counts.
  const query = useQuery({
    queryKey: ["cil", "change-events", direction, jobId ?? "everything", "all"],
    queryFn: () => fetchCilChangeEvents({ direction, view: "all", ingestionJobId: jobId }),
  });
  const [openDoc, setOpenDoc] = React.useState<CilDocumentLink | null>(null);
  // Org-wide setting (Settings > Dashboard home): whether the search box and sort menu are shown.
  const settingsQuery = useQuery({ queryKey: ["alert-settings"], queryFn: () => getAlertSettings() });
  // Settings: events with no HP order on the cost are hidden unless the box is unticked (the API leaves them out too; this keeps an older API's lists the same).
  const events = useVisibleEvents(query.data, settingsQuery.data?.hide_no_hp_order_events !== false);
  const showToolbar = settingsQuery.data?.show_event_search !== false;
  const search = params.get("q") ?? "";
  const sort = sortFrom(params);
  const updateParams = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  const copy = jobId ? FILE_COPY[direction] : COPY[direction];
  const view: ListTab = asked ?? TABS.find((t) => (events ?? []).some(t.keep))?.view ?? "needs_review";
  const tab = TABS.find((t) => t.view === view) ?? TABS[0];
  const inTab = events?.filter(tab.keep) ?? [];
  // Search and sort apply only while the toolbar is shown; hidden, the list is the newest ruling first.
  const dir = dirFrom(params, sort);
  const rows = showToolbar ? sortEvents(inTab.filter((e) => matchesSearch(e, search)), sort, dir) : sortEvents(inTab, "date", "desc");
  return (
    <div className="page-stack">
      <PageHeader title={copy.title} description={copy.description} />
      {SHOW_COVERAGE_LINK ? <CoverageLine /> : null}
      {/* Two fixed columns: tabs on the left (they wrap inside their column), search and sort pinned to the top right of the same row. */}
      <div className={["grid items-start gap-3", showToolbar ? "grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-1"].join(" ")}>
      <div role="tablist" aria-label="Which changed events" className="flex min-w-0 flex-wrap gap-2">
        {TABS.filter((t) => !t.hideWhenEmpty || t.view === view || (events ?? []).some(t.keep)).map((t) => (
          <HoverHint key={t.view} hint={t.hint} delayMs={1000}>
          <button
            role="tab"
            aria-selected={view === t.view}
            onClick={() => setParams({ view: t.view }, { replace: true })}
            className={[
              "rounded-md px-3 py-1.5 text-sm font-medium",
              view === t.view ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
            ].join(" ")}
          >
            {t.label}
            {events ? (
              <span className={["ml-1.5 tabular-nums", view === t.view ? "text-zinc-300" : "text-zinc-500"].join(" ")}>
                {events.filter(t.keep).length}
              </span>
            ) : null}
          </button>
          </HoverHint>
        ))}
      </div>
      {showToolbar ? (
        <ListToolbar
          query={search}
          onQuery={(value) => updateParams({ q: value || null })}
          sort={sort}
          onSort={(field, direction) => updateParams({ sort: field === "date" ? null : field, dir: direction === defaultDir(field) ? null : direction })}
          dir={dir}
          shown={rows.length}
          total={inTab.length}
        />
      ) : null}
      </div>
      {query.isPending ? <LoadingState label="Loading changed events..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load changed events" /> : null}
      {query.isSuccess && rows.length === 0 ? (
        <EmptyState>{showToolbar && search.trim() && inTab.length ? `No changed events match "${search.trim()}".` : events?.length ? tab.empty : copy.empty}</EmptyState>
      ) : null}
      {query.isSuccess && rows.length > 0 ? (
        <DocumentLinks onOpen={setOpenDoc}>
          <ImpactTable events={rows} detailHref={(event) => `/popup/${SLUG[event.direction]}/${event.id}${listQuery(view, params)}${jobId ? `${listQuery(view, params) ? "&" : "?"}file=${jobId}` : ""}`} />
        </DocumentLinks>
      ) : null}
      {openDoc ? <RulingDocumentViewer link={openDoc} onClose={() => setOpenDoc(null)} /> : null}
    </div>
  );
}

/** A ruling's own order, opened from its name in the text (whole document,
 * at the page where it decides this cost when known). */
function RulingDocumentViewer({ link, onClose }: { link: CilDocumentLink; onClose: () => void }) {
  return (
    <SourceDocumentViewer
      source={{ type: "decision", id: link.decision_id, label: link.page ? `${link.text} · p${link.page}` : link.text }}
      onClose={onClose}
      startPage={link.page}
    />
  );
}

/** The "Every ruling checked" link is hidden for now (its data is not
 * fetched); flip to bring it back. The page stays at /popup/cil-coverage. */
const SHOW_COVERAGE_LINK = false;

/** "Every ruling checked" link, with how many readings still wait for a
 * person: no ruling is left unaddressed. */
function CoverageLine() {
  const query = useQuery({ queryKey: ["cil", "coverage"], queryFn: fetchCilCoverage });
  const rows = query.data ?? [];
  const waiting = rows.reduce((n, r) => n + r.waiting_review, 0);
  return (
    <p className="text-sm text-zinc-700">
      <Link to="/popup/cil-coverage" className="font-medium text-[var(--color-accent-content)] hover:underline">
        Every ruling checked{query.isSuccess ? ` (${rows.length})` : ""} →
      </Link>
      {query.isSuccess ? (
        <span className={waiting ? "ml-2 text-amber-800" : "ml-2 text-zinc-500"}>
          {waiting
            ? `${waiting} reading${waiting === 1 ? "" : "s"} still waiting for review; they are compared with HP once approved.`
            : "Every reading has been reviewed."}
        </span>
      ) : null}
    </p>
  );
}

const OVERVIEW_MONEY = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const crore = (value: number | null) => (value === null ? "—" : OVERVIEW_MONEY.format(value));

/** The HP order behind an event, in one place: HP's position on the event's cost, and every claimed-vs-allowed line of the order with
 * the event's own cost marked. Copies of the same order that were read are counted once. */
function OrderBehindPosition({ eventId }: { eventId: string }) {
  const [open, setOpen] = React.useState(true);
  const query = useQuery({ queryKey: ["cil", "order-summary", eventId], queryFn: () => fetchCilOrderSummary(eventId) });
  if (query.isPending || query.isError) return null; // an extra panel: never in the way of the event itself
  const summary = query.data;
  if (!summary.available) return null;
  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-900">The order behind HP's position</h3>
          <span className="text-xs text-zinc-600">{summary.order_label}</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "Hide" : "Show"}
          </Button>
        </div>
        {open ? (
          <div className="mt-2 space-y-3">
            {summary.positions.length ? (
              <div className="text-sm text-zinc-800">
                <p className="mb-1 font-medium">HP's position on {summary.cost_label ?? "this cost"}:</p>
                <ul className="list-disc space-y-0.5 pl-5">
                  {summary.positions.map((p) => (
                    <li key={p.period}>
                      {p.period}: <span className="font-medium">{p.result}</span>
                      {p.block ? <span className="text-zinc-500"> ({p.block})</span> : null}
                      {p.decision_page ? <span className="text-zinc-500"> · p.{p.decision_page}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {summary.lines.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
                      <th className="py-1.5 pr-3 font-medium">Cost</th>
                      <th className="py-1.5 pr-3 font-medium">Year</th>
                      <th className="py-1.5 pr-3 font-medium">Block</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Claimed (Rs Cr)</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Allowed (Rs Cr)</th>
                      <th className="py-1.5 pr-3 text-right font-medium">Gap</th>
                      <th className="py-1.5 pr-3 font-medium">Why cut</th>
                      <th className="py-1.5 font-medium">Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.lines.map((l) => (
                      <tr key={`${l.component}-${l.financial_year}-${l.block}-${l.kind}`} className={["border-b border-zinc-100 align-top", l.is_event_cost ? "bg-amber-50" : ""].join(" ")}>
                        <td className="py-1.5 pr-3 font-medium text-zinc-900">
                          {l.label}
                          {l.is_event_cost ? <Badge tone="warning" className="ml-1.5">this event</Badge> : null}
                          {l.note ? <span className="mt-0.5 block text-xs font-normal text-zinc-600">{l.note}</span> : null}
                        </td>
                        <td className="py-1.5 pr-3 text-zinc-700">{l.financial_year}</td>
                        <td className="py-1.5 pr-3 text-zinc-700">{l.block}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{crore(l.claimed_cr)}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{crore(l.allowed_cr)}</td>
                        <td className={["py-1.5 pr-3 text-right tabular-nums", l.material ? "font-semibold text-red-700" : "text-zinc-600"].join(" ")}>{l.gap_cr === null ? "—" : crore(l.gap_cr)}</td>
                        <td className="py-1.5 pr-3 text-zinc-700">{l.reason ?? "—"}</td>
                        <td className="py-1.5 tabular-nums text-zinc-600">{l.page ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <p className="text-xs text-zinc-500">
              One row per cost, year and block, from the order's own tables. A gap in red is a material cut (Rs 1 Cr or 1% of the claim); a gap with a note is
              flagged for a person, not counted as a cut.
              {summary.retired_copies.length ? ` Read once: ${summary.retired_copies.length} other file${summary.retired_copies.length === 1 ? "" : "s"} of the same order ${summary.retired_copies.length === 1 ? "was" : "were"} set aside (${summary.retired_copies.join(", ")}).` : ""}
            </p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** One changed event on its own page (opened from the table), with Back to
 * the list it came from - same tab of the list, same window. */
export function CilChangeEventDetailPage({ direction }: { direction: CilDirection }) {
  const { eventId = "" } = useParams();
  const [params] = useSearchParams();
  const view = viewFrom(params);
  const [viewing, setViewing] = React.useState<CilDecision | null>(null);
  const [openDoc, setOpenDoc] = React.useState<CilDocumentLink | null>(null);
  const query = useQuery({
    queryKey: ["cil", "change-event", eventId],
    queryFn: () => fetchCilChangeEvent(eventId),
    enabled: Boolean(eventId),
  });
  const fromFile = params.get("file");
  // Opened from the Overview's tabs: Back returns to the same tab, search and sort there.
  const fromOverview = params.get("from") === "overview";
  const backTo = fromOverview
    ? `/overview${overviewQuery(overviewTabFrom(params), params)}`
    : fromFile ? `/popup/cil-file/${fromFile}/${direction === "disallowed_to_allowed" ? "from-disallowed" : "from-allowed"}${listQuery(view, params)}` : `/popup/${SLUG[direction]}${listQuery(view, params)}`;
  return (
    <div className="page-stack">
      <div>
        <Link to={backTo} className="text-xs font-medium text-[var(--color-accent-content)] hover:underline">
          ← Back to {fromOverview ? "Overview" : fromFile ? FILE_COPY[direction].title : COPY[direction].title}
        </Link>
      </div>
      {query.isPending ? <LoadingState label="Loading the changed event..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load this changed event" /> : null}
      {query.isSuccess && query.data === null ? <EmptyState>This changed event no longer exists.</EmptyState> : null}
      {query.isSuccess && query.data ? (
        <DocumentLinks links={query.data.documents ?? []} parties={eventParties(query.data)} onOpen={setOpenDoc}>
          <ChangeEventCard event={query.data} onViewSource={setViewing} />
          <OrderBehindPosition eventId={query.data.id} />
        </DocumentLinks>
      ) : null}
      {openDoc ? <RulingDocumentViewer link={openDoc} onClose={() => setOpenDoc(null)} /> : null}
      {viewing ? (
        <SourceDocumentViewer
          source={{ type: "decision", id: viewing.id, label: `${viewing.forum_name ?? ""} ${viewing.case_ref ?? ""} · p${viewing.page}`.trim() }}
          onClose={() => setViewing(null)}
          passage={{
            text: viewing.quote,
            page: viewing.page,
            caution:
              viewing.source_path === "legacy"
                ? "This decision came from an earlier summary of the order, not a verbatim quote, so the exact words may not appear on the page."
                : null,
          }}
        />
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Overview: every changed event, in three tabs by what a person has done with it
// ---------------------------------------------------------------------------

type OverviewTab = "in_review" | "approved" | "rejected";

const OVERVIEW_TABS: Array<{ view: OverviewTab; label: string; hint: string; empty: string; keep: (e: CilChangeEvent) => boolean }> = [
  {
    view: "in_review", label: "In Review",
    hint: "Raised by the system and waiting for a person, or a reviewer has marked it \"under review\" and is checking it.",
    empty: "Nothing is waiting for review.", keep: (e) => e.status === "new" || e.status === "under_review",
  },
  {
    view: "approved", label: "Approved",
    hint: "Accepted by a reviewer; for an opportunity a claim is opened.",
    empty: "Nothing has been approved yet.", keep: (e) => e.status === "confirmed",
  },
  {
    view: "rejected", label: "Rejected",
    hint: "Rejected by a reviewer as not relevant to HP, with a reason.",
    empty: "Nothing has been rejected.", keep: (e) => e.status === "dismissed",
  },
];

/** The Overview tab named in the URL (`?tab=`), or null when none is (the first tab with anything in it opens). */
function overviewTabFrom(params: URLSearchParams): OverviewTab | null {
  const value = params.get("tab") ?? "";
  return OVERVIEW_TABS.some((t) => t.view === value) ? (value as OverviewTab) : null;
}

/** The Overview's tab, search and sort as a query string, so opening an event and coming Back restores them. */
function overviewQuery(tab: OverviewTab | null, params?: URLSearchParams): string {
  const out = new URLSearchParams();
  if (tab) out.set("tab", tab);
  for (const key of ["q", "sort", "dir"]) {
    const value = params?.get(key);
    if (value) out.set(key, value);
  }
  const text = out.toString();
  return text ? `?${text}` : "";
}

/** The events without those that have no HP order on the cost (`trigger_scope` "unclaimed") when the setting hides them. */
function useVisibleEvents(events: CilChangeEvent[] | undefined, hide: boolean): CilChangeEvent[] | undefined {
  return React.useMemo(() => (hide ? events?.filter((e) => e.trigger_scope !== "unclaimed") : events), [events, hide]);
}

/** Every changed event (both directions) in three tabs: In Review, Approved, Rejected. Shown on the Overview page. */
export function CilEventsOverview() {
  const [params, setParams] = useSearchParams();
  const asked = overviewTabFrom(params);
  const query = useQuery({
    queryKey: ["cil", "change-events", "overview", "all"],
    queryFn: () => fetchCilChangeEvents({ view: "all" }),
    refetchInterval: 10_000, // follows each file as it finishes reading
  });
  const [openDoc, setOpenDoc] = React.useState<CilDocumentLink | null>(null);
  const columnChoice = useColumnChoice();
  const settingsQuery = useQuery({ queryKey: ["alert-settings"], queryFn: () => getAlertSettings() });
  const events = useVisibleEvents(query.data, settingsQuery.data?.hide_no_hp_order_events !== false);
  const showToolbar = settingsQuery.data?.show_event_search !== false;
  const search = params.get("q") ?? "";
  // The Overview opens highest confidence first; the sort menu changes it.
  const sort = sortFrom(params, "confidence");
  const updateParams = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  const view: OverviewTab = asked ?? OVERVIEW_TABS.find((t) => (events ?? []).some(t.keep))?.view ?? "in_review";
  const tab = OVERVIEW_TABS.find((t) => t.view === view) ?? OVERVIEW_TABS[0];
  const inTab = events?.filter(tab.keep) ?? [];
  const dir = dirFrom(params, sort);
  const rows = showToolbar ? sortEvents(inTab.filter((e) => matchesSearch(e, search)), sort, dir) : sortEvents(inTab, "confidence", "desc");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div role="tablist" aria-label="Changed events by review state" className="flex min-w-0 flex-wrap gap-2">
          {OVERVIEW_TABS.map((t) => (
            <HoverHint key={t.view} hint={t.hint} delayMs={1000}>
              <button
                role="tab"
                aria-selected={view === t.view}
                onClick={() => updateParams({ tab: t.view })}
                className={["rounded-md px-3 py-1.5 text-sm font-medium", view === t.view ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"].join(" ")}
              >
                {t.label}
                {events ? (
                  <span className={["ml-1.5 tabular-nums", view === t.view ? "text-zinc-300" : "text-zinc-500"].join(" ")}>{events.filter(t.keep).length}</span>
                ) : null}
              </button>
            </HoverHint>
          ))}
        </div>
        {showToolbar ? (
          <ListToolbar
            query={search}
            onQuery={(value) => updateParams({ q: value || null })}
            sort={sort}
            onSort={(field, direction) => updateParams({ sort: field === "confidence" ? null : field, dir: direction === defaultDir(field) ? null : direction })}
            dir={dir}
            shown={rows.length}
            total={inTab.length}
            columnChoice={columnChoice}
          />
        ) : (
          <ColumnsMenu choice={columnChoice} />
        )}
      </div>
      {query.isPending ? <LoadingState label="Loading changed events..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load changed events" /> : null}
      {query.isSuccess && rows.length === 0 ? (
        <EmptyState>{showToolbar && search.trim() && inTab.length ? `No changed events match "${search.trim()}".` : tab.empty}</EmptyState>
      ) : null}
      {query.isSuccess && rows.length > 0 ? (
        <DocumentLinks onOpen={setOpenDoc}>
          <ImpactTable
            heading={false}
            columns={columnChoice.columns}
            events={rows}
            detailHref={(event) => `/popup/${SLUG[event.direction]}/${event.id}${overviewQuery(view, params)}${overviewQuery(view, params) ? "&" : "?"}from=overview`}
          />
        </DocumentLinks>
      ) : null}
      {openDoc ? <RulingDocumentViewer link={openDoc} onClose={() => setOpenDoc(null)} /> : null}
    </div>
  );
}
